import moment from "moment";
import { getMonthsInRange, getWeeksInYearRange } from "./date";

export const getMilestonePosition = (targetDate, viewType, displayYearRange) => {
  const MONTH_WIDTH = 241.5;
  const WEEK_WIDTH = 241;
  const QUARTER_WIDTH = 495;
  const YEAR_QUARTER_WIDTH = 125.25;
  
  const milestoneDate = moment(targetDate);
  const startYear = displayYearRange?.start;
  const endYear = displayYearRange?.end;

  // Check if milestone is within range
  if (milestoneDate.year() < startYear || milestoneDate.year() > endYear) {
    return null;
  }

  switch (viewType.toLowerCase()) {
    case "monthly":
    case "month": {
      const months = getMonthsInRange(startYear, endYear);
      let position = 0;

      for (let i = 0; i < months.length; i++) {
        const month = months[i];
        const daysInMonth = month.days;
        const dayWidth = MONTH_WIDTH / daysInMonth;

        // Check if milestone is in this month
        if (
          milestoneDate.year() === month.year &&
          milestoneDate.month() === month.startDate.getMonth()
        ) {
          position += milestoneDate.date() * dayWidth;
          break;
        } else {
          position += MONTH_WIDTH;
        }
      }

      return position;
    }

    case "quarter": {
      const quarters = [
        { q: 1, startMonth: 0, endMonth: 2 },
        { q: 2, startMonth: 3, endMonth: 5 },
        { q: 3, startMonth: 6, endMonth: 8 },
        { q: 4, startMonth: 9, endMonth: 11 },
      ];
      let position = 0;

      for (let year = startYear; year <= endYear; year++) {
        for (const quarter of quarters) {
          const quarterStart = moment({ year, month: quarter.startMonth, day: 1 }).startOf("day");
          const quarterEnd = moment({ year, month: quarter.endMonth }).endOf("month").startOf("day");

          if (milestoneDate.isBetween(quarterStart, quarterEnd, undefined, "[]")) {
            const totalDaysInQuarter = quarterEnd.diff(quarterStart, "days") + 1;
            const dayOfQuarter = milestoneDate.diff(quarterStart, "days") + 1;
            const dayWidth = QUARTER_WIDTH / totalDaysInQuarter;

            position += dayOfQuarter * dayWidth;
            return position;
          } else {
            position += QUARTER_WIDTH;
          }
        }
      }

      return position;
    }

    case "year": {
      const quarters = [
        { q: 1, startMonth: 0, endMonth: 2 },
        { q: 2, startMonth: 3, endMonth: 5 },
        { q: 3, startMonth: 6, endMonth: 8 },
        { q: 4, startMonth: 9, endMonth: 11 },
      ];
      let position = 0;

      for (let year = startYear; year <= endYear; year++) {
        for (const quarter of quarters) {
          const quarterStart = moment({ year, month: quarter.startMonth, day: 1 }).startOf("day");
          const quarterEnd = moment({ year, month: quarter.endMonth }).endOf("month").startOf("day");

          if (milestoneDate.isBetween(quarterStart, quarterEnd, undefined, "[]")) {
            const totalDaysInQuarter = quarterEnd.diff(quarterStart, "days") + 1;
            const dayOfQuarter = milestoneDate.diff(quarterStart, "days") + 1;
            const dayWidth = YEAR_QUARTER_WIDTH / totalDaysInQuarter;

            position += dayOfQuarter * dayWidth;
            return position;
          } else {
            position += YEAR_QUARTER_WIDTH;
          }
        }
      }

      return position;
    }

    case "weekly":
    case "week": {
      const weeks = getWeeksInYearRange(startYear, endYear);
      const milestoneDateStr = milestoneDate.format("YYYY-MM-DD");
      let position = 0;

      for (const week of weeks) {
        const dayIndex = week.findIndex(
          (d) => moment(d).format("YYYY-MM-DD") === milestoneDateStr
        );

        const dayWidth = WEEK_WIDTH / week.length;

        if (dayIndex !== -1) {
          position += (dayIndex + 1) * dayWidth;
          break;
        } else {
          position += WEEK_WIDTH;
        }
      }

      return position - 8; // Adjust for week view offset
    }

    default: {
      const DAY_WIDTH = 241;
      const start = moment().year(startYear).startOf("year");
      return (milestoneDate.diff(start, "days") + 1) * DAY_WIDTH;
    }
  }
};

export const groupMilestonesByDate = (milestones, viewType, displayYearRange) => {
  const grouped = {};
  
  milestones?.forEach((milestone) => {
    const position = getMilestonePosition(milestone.targetDate, viewType, displayYearRange);
    
    if (position !== null) {
      const key = `${position}`;
      if (!grouped[key]) {
        grouped[key] = {
          position,
          milestones: [],
        };
      }
      grouped[key].milestones.push(milestone);
    }
  });
  
  return Object.values(grouped);
};

// function for Portfolio / Prorgam timelines
export const getSharedMilestonePosition = (targetDate, viewType, displayYearRange, dynamicWidths = null) => {
  const MONTH_WIDTH = 241;
  const WEEK_WIDTH = 241;
  const QUARTER_WIDTH = 500;
  
  // Use dynamic width for year view if available
  const getYearQuarterWidth = () => {
    return dynamicWidths?.useDynamic ? dynamicWidths.quarterWidth : 125.25;
  };
  
  const milestoneDate = moment(targetDate);
  const startYear = displayYearRange?.start;
  const endYear = displayYearRange?.end;

  // Check if milestone is within range
  if (milestoneDate.year() < startYear || milestoneDate.year() > endYear) {
    return null;
  }

  switch (viewType.toLowerCase()) {
    case "monthly":
    case "month": {
      const months = getMonthsInRange(startYear, endYear);
      let position = 0;

      for (let i = 0; i < months.length; i++) {
        const month = months[i];
        const daysInMonth = month.days;
        const dayWidth = MONTH_WIDTH / daysInMonth;

        // Check if milestone is in this month
        if (
          milestoneDate.year() === month.year &&
          milestoneDate.month() === month.startDate.getMonth()
        ) {
          position += milestoneDate.date() * dayWidth;
          break;
        } else {
          position += MONTH_WIDTH;
        }
      }

      return position - 7; // Adjust for month view offset
    }

    case "quarter": {
      const offSet = 0;
      const quarters = [
        { q: 1, startMonth: 0, endMonth: 2 },
        { q: 2, startMonth: 3, endMonth: 5 },
        { q: 3, startMonth: 6, endMonth: 8 },
        { q: 4, startMonth: 9, endMonth: 11 },
      ];
      let position = 0;
    
      for (let year = startYear; year <= endYear; year++) {
        for (const quarter of quarters) {
          const quarterStart = moment({ year, month: quarter.startMonth, day: 1 }).startOf("day");
          const quarterEnd = moment({ year, month: quarter.endMonth }).endOf("month").startOf("day");
    
          if (milestoneDate.isBetween(quarterStart, quarterEnd, undefined, "[]")) {
            const totalDaysInQuarter = quarterEnd.diff(quarterStart, "days") + 1;
            const dayOfQuarter = milestoneDate.diff(quarterStart, "days");
            const dayWidth = QUARTER_WIDTH / totalDaysInQuarter;
    
            position += (dayOfQuarter * dayWidth) + dayWidth;
            return position - offSet;
          } else {
            position += QUARTER_WIDTH;
          }
        }
      }
    
      return position - offSet;
    }

    case "year": {
      const YEAR_QUARTER_WIDTH = getYearQuarterWidth();
      const quarters = [
        { q: 1, startMonth: 0, endMonth: 2 },
        { q: 2, startMonth: 3, endMonth: 5 },
        { q: 3, startMonth: 6, endMonth: 8 },
        { q: 4, startMonth: 9, endMonth: 11 },
      ];
      let position = 0;

      for (let year = startYear; year <= endYear; year++) {
        for (const quarter of quarters) {
          const quarterStart = moment({ year, month: quarter.startMonth, day: 1 }).startOf("day");
          const quarterEnd = moment({ year, month: quarter.endMonth }).endOf("month").startOf("day");

          if (milestoneDate.isBetween(quarterStart, quarterEnd, undefined, "[]")) {
            const totalDaysInQuarter = quarterEnd.diff(quarterStart, "days") + 1;
            const dayOfQuarter = milestoneDate.diff(quarterStart, "days") + 1;
            const dayWidth = YEAR_QUARTER_WIDTH / totalDaysInQuarter;

            position += dayOfQuarter * dayWidth;
            return position;
          } else {
            position += YEAR_QUARTER_WIDTH;
          }
        }
      }

      return position;
    }

    case "weekly":
    case "week": {
      const weeks = getWeeksInYearRange(startYear, endYear);
      const milestoneDateStr = milestoneDate.format("YYYY-MM-DD");
      let position = 0;

      for (const week of weeks) {
        const dayIndex = week.findIndex(
          (d) => moment(d).format("YYYY-MM-DD") === milestoneDateStr
        );

        const dayWidth = WEEK_WIDTH / week.length;

        if (dayIndex !== -1) {
          position += (dayIndex + 1) * dayWidth;
          break;
        } else {
          position += WEEK_WIDTH;
        }
      }

      return position - 15; // Adjust for week view offset
    }

    default: {
      const DAY_WIDTH = 241;
      const start = moment().year(startYear).startOf("year");
      return (milestoneDate.diff(start, "days") + 1) * DAY_WIDTH;
    }
  }
};

export const groupSharedMilestonesByDate = (milestones, viewType, displayYearRange) => {
  const grouped = {};
  
  milestones?.forEach((milestone) => {
    const position = getSharedMilestonePosition(milestone.targetDate, viewType, displayYearRange);
    
    if (position !== null) {
      const key = `${position}`;
      if (!grouped[key]) {
        grouped[key] = {
          position,
          milestones: [],
        };
      }
      grouped[key].milestones.push(milestone);
    }
  });
  
  return Object.values(grouped);
};